/**
 * Utility functions for debug mode timestamp comparisons.
 * These are pure functions that can be easily tested.
 */

const { DEV, VITE_DEBUG_LOGGER } = import.meta.env;

/**
 * Local-development override for debug mode.
 *
 * When `VITE_DEBUG_LOGGER=true` is set in a local `.env.development` file,
 * debug mode is forced on regardless of the user's `debugModeUntil` timestamp.
 * Gated behind Vite's `DEV` flag so it can never enable verbose logging,
 * Sentry logs, or session replay in a production build.
 *
 * @returns true only in a dev build with the env flag explicitly enabled
 */
export function isLocalDebugForced(): boolean {
  if (!DEV) {
    return false;
  }

  return VITE_DEBUG_LOGGER === 'true' || VITE_DEBUG_LOGGER === true;
}

/**
 * Determines if debug mode is currently active based on the expiration timestamp.
 *
 * @param debugModeUntil - ISO datetime string or null
 * @returns true if debug mode is active (timestamp is in the future)
 */
export function isDebugModeActive(debugModeUntil: string | null): boolean {
  if (isLocalDebugForced()) {
    return true;
  }

  if (!debugModeUntil) {
    return false;
  }

  const expirationDate = new Date(debugModeUntil);

  // Check for invalid date
  if (Number.isNaN(expirationDate.getTime())) {
    return false;
  }

  return expirationDate.getTime() > Date.now();
}

/**
 * Calculates the remaining time in milliseconds until debug mode expires.
 * Returns 0 if debug mode is not active or already expired.
 *
 * @param debugModeUntil - ISO datetime string or null
 * @returns milliseconds until expiration, or 0 if expired/inactive
 */
export function getDebugModeRemainingMs(debugModeUntil: string | null): number {
  // When forced locally there is no expiration; keep debug mode on indefinitely.
  if (isLocalDebugForced()) {
    return 0;
  }

  if (!debugModeUntil) {
    return 0;
  }

  const expirationDate = new Date(debugModeUntil);

  // Check for invalid date
  if (Number.isNaN(expirationDate.getTime())) {
    return 0;
  }

  const remainingMs = expirationDate.getTime() - Date.now();
  return Math.max(0, remainingMs);
}
